import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { DayPicker } from "react-day-picker"
import { cn } from "@/lib/utils"

const MONTHS = [
  "January", "February", "March", "April",
  "May", "June", "July", "August",
  "September", "October", "November", "December",
]

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  mode = "single",
  selected: selectedProp,
  onSelect: onSelectProp,
  // Year picker range. Defaults suit birthdates; booking pickers pass a
  // forward-looking range (e.g. this year → +5).
  minYear = 1900,
  maxYear = new Date().getFullYear(),
  ...props
}) {
  const [internalSelected, setInternalSelected] = React.useState(undefined)
  const isControlled = selectedProp !== undefined || onSelectProp !== undefined
  const selected = isControlled ? selectedProp : internalSelected
  const onSelect = isControlled ? onSelectProp : setInternalSelected

  const [month, setMonth] = React.useState(props.defaultMonth ?? new Date())
  const [pickerOpen, setPickerOpen] = React.useState(false)
  const [pickerView, setPickerView] = React.useState("month")

  // No hardcoded fallback. Overlay does not render until this is measured,
  // so there is no "guessed 52px" window where it can sit half-on-top of
  // the nav buttons and eat half their clicks.
  /** @type {[number | null, React.Dispatch<React.SetStateAction<number | null>>]} */
  const [overlayTop, setOverlayTop] = React.useState(null)

  // Full rendered height of the calendar box, so the overlay can be told
  // to cover at least that much (no sliver of the day grid peeking out
  // below a shorter overlay, e.g. the Month view) while still being free
  // to grow taller when a view's content needs more room (e.g. the Year
  // view's paginated grid).
  /** @type {React.RefObject<HTMLDivElement | null>} */
  const wrapperRef = React.useRef(null)
  const [calendarHeight, setCalendarHeight] = React.useState(null)

  React.useLayoutEffect(() => {
    if (wrapperRef.current) {
      setCalendarHeight(wrapperRef.current.offsetHeight)
    }
  }, [month])

  const currentYear = month.getFullYear()
  const currentMonth = month.getMonth()

  const MIN_YEAR = minYear
  const MAX_YEAR = maxYear
  const YEAR_PAGE_SIZE = 12

  const pageStartFor = React.useCallback(
    (year) => MIN_YEAR + Math.floor((year - MIN_YEAR) / YEAR_PAGE_SIZE) * YEAR_PAGE_SIZE,
    []
  )

  const [yearPageStart, setYearPageStart] = React.useState(() => pageStartFor(currentYear))

  // Re-center the decade page on whichever year is currently in view
  // whenever the year picker is (re)opened.
  React.useEffect(() => {
    if (pickerOpen && pickerView === "year") {
      setYearPageStart(pageStartFor(currentYear))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerOpen, pickerView])

  const canGoPrevYearPage = yearPageStart > MIN_YEAR
  const canGoNextYearPage = yearPageStart + YEAR_PAGE_SIZE <= MAX_YEAR

  const yearPage = Array.from({ length: YEAR_PAGE_SIZE }, (_, i) => yearPageStart + i)

  function handleMonthSelect(idx) {
    setMonth(new Date(currentYear, idx, 1))
    setPickerOpen(false)
  }

  function handleYearSelect(year) {
    setMonth(new Date(year, currentMonth, 1))
    setPickerView("month")
  }

  const MonthCaption = ({ calendarMonth }) => {
    /** @type {React.RefObject<HTMLDivElement | null>} */
    const captionRef = React.useRef(null)
    const displayDate = calendarMonth?.date ?? month
    const label = displayDate.toLocaleString("default", { month: "long", year: "numeric" })

    // Measure where this caption row actually ends (top + height), and use
    // that as the overlay's top offset. This replaces the old hardcoded
    // `top: "52px"` which silently drifted from the real layout whenever
    // fonts loaded late, zoom level changed, or OS rendered the Tailwind
    // classes slightly differently — causing the overlay to bleed upward
    // into the bottom half of the nav buttons and swallow clicks there.
    React.useLayoutEffect(() => {
      if (captionRef.current) {
        const top = captionRef.current.offsetTop + captionRef.current.offsetHeight
        setOverlayTop(top)
      }
    }, [calendarMonth])

    return (
      <div ref={captionRef} className="relative flex items-center justify-center h-9">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setPickerView("month")
            setPickerOpen((v) => !v)
          }}
          className="flex items-center gap-1 text-sm font-semibold text-white tracking-wide px-2 py-1 rounded-md hover:bg-white/10 transition-colors cursor-pointer"
        >
          {label}
          <svg
            width="12" height="12" viewBox="0 0 12 12" fill="none"
            style={{ transform: pickerOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}
          >
            <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    )
  }

  return (
    <div ref={wrapperRef} className="relative w-fit">
      <DayPicker
        mode={mode}
        selected={selected}
        onSelect={onSelect}
        showOutsideDays={showOutsideDays}
        month={month}
        onMonthChange={setMonth}
        className={cn(
          "p-4 rounded-2xl bg-[#1e3d2e] text-white shadow-xl w-fit select-none",
          className
        )}
        classNames={{
          months: "flex flex-col",
          month: "space-y-3",
          month_caption: "relative flex items-center justify-center h-9",
          caption_label: "text-sm font-semibold text-white tracking-wide",
          nav: "absolute inset-x-0 top-2 right-3 left-3 flex items-center justify-between px-1 h-9 z-20 pointer-events-none",
          button_previous:
            "relative pointer-events-auto h-7 w-7 rounded-md flex items-center justify-center bg-transparent text-white/60 hover:text-white hover:bg-white/10 transition-colors border border-white/10",
          button_next:
            "relative pointer-events-auto h-7 w-7 rounded-md flex items-center justify-center bg-transparent text-white/60 hover:text-white hover:bg-white/10 transition-colors border border-white/10",
          weekdays: "flex",
          weekday: "w-9 h-9 flex items-center justify-center text-xs font-medium text-white/50",
          weeks: "space-y-0.5",
          week: "flex",
          day: "w-9 h-9 p-0 flex items-center justify-center relative",
          day_button:
            "w-8 h-8 rounded-md text-sm text-white transition-all flex items-center justify-center cursor-pointer focus:outline-none border border-transparent hover:border-emerald-500",
          today:
            "[&>button]:bg-emerald-700 [&>button]:!border [&>button]:!border-emerald-500 [&>button]:font-semibold [&>button]:rounded-md",
          selected:
            "[&>button]:!bg-[#d4a017] [&>button]:!text-[#1a2e20] [&>button]:font-semibold [&>button]:rounded-md [&>button]:!border-transparent [&>button]:hover:!border-transparent",
          outside: "[&>button]:text-white/25 [&>button]:hover:border-white/20",
          disabled:
            "[&>button]:text-white/20 [&>button]:cursor-not-allowed [&>button]:hover:!border-transparent",
          hidden: "invisible",
          range_start:
            "[&>button]:!bg-[#d4a017] [&>button]:!text-[#1a2e20] [&>button]:rounded-md [&>button]:!border-transparent",
          range_end:
            "[&>button]:!bg-[#d4a017] [&>button]:!text-[#1a2e20] [&>button]:rounded-md [&>button]:!border-transparent",
          range_middle: "bg-yellow-900/30 [&>button]:hover:border-emerald-500",
          ...classNames,
        }}
        components={{
          Chevron: ({ orientation }) =>
            orientation === "left" ? (
              <ChevronLeft className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            ),
          MonthCaption,
        }}
        {...props}
      />

      {/* Only render once overlayTop is actually measured. No guessed
          fallback number, so there's no window where this can overlap
          and steal clicks from the bottom half of the nav buttons. */}
      {pickerOpen && overlayTop !== null && (
        <div
          className="absolute left-0 right-0 rounded-b-2xl bg-[#1e3d2e] z-10 flex flex-col p-3 shadow-xl"
          style={{
            top: `${overlayTop}px`,
            minHeight:
              calendarHeight !== null
                ? `${Math.max(calendarHeight - overlayTop, 0)}px`
                : undefined,
          }}
        >
          <div className="flex items-center justify-between mb-3 px-1">
            <div className="flex rounded-lg overflow-hidden border border-white/10">
              <button
                type="button"
                onClick={() => setPickerView("month")}
                className={cn(
                  "px-3 py-1 text-xs font-medium transition-colors",
                  pickerView === "month"
                    ? "bg-emerald-700 text-white"
                    : "text-white/60 hover:text-white hover:bg-white/10"
                )}
              >
                Month
              </button>
              <button
                type="button"
                onClick={() => setPickerView("year")}
                className={cn(
                  "px-3 py-1 text-xs font-medium transition-colors",
                  pickerView === "year"
                    ? "bg-emerald-700 text-white"
                    : "text-white/60 hover:text-white hover:bg-white/10"
                )}
              >
                Year
              </button>
            </div>
            <button
              type="button"
              onClick={() => setPickerOpen(false)}
              className="h-7 w-7 rounded-md flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors border border-white/10"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          {pickerView === "month" && (
            <div className="grid grid-cols-3 gap-2 flex-1 content-start">
              {MONTHS.map((name, idx) => (
                <button
                  type="button"
                  key={name}
                  onClick={() => handleMonthSelect(idx)}
                  className={cn(
                    "py-2 rounded-lg text-sm font-medium transition-all border",
                    idx === currentMonth
                      ? "bg-[#d4a017] text-[#1a2e20] border-transparent"
                      : "text-white/80 border-white/10 hover:bg-white/10 hover:border-white/20"
                  )}
                >
                  {name.slice(0, 3)}
                </button>
              ))}
            </div>
          )}

          {pickerView === "year" && (
            <div className="flex flex-col flex-1">
              <div className="flex items-center justify-between mb-2 px-1">
                <button
                  type="button"
                  disabled={!canGoPrevYearPage}
                  onClick={() =>
                    setYearPageStart((s) => Math.max(MIN_YEAR, s - YEAR_PAGE_SIZE))
                  }
                  className="h-6 w-6 rounded-md flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors border border-white/10 disabled:opacity-30 disabled:pointer-events-none"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </button>
                <span className="text-xs font-semibold text-white/80 tracking-wide">
                  {yearPageStart} &ndash; {yearPageStart + YEAR_PAGE_SIZE - 1}
                </span>
                <button
                  type="button"
                  disabled={!canGoNextYearPage}
                  onClick={() =>
                    setYearPageStart((s) => s + YEAR_PAGE_SIZE)
                  }
                  className="h-6 w-6 rounded-md flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors border border-white/10 disabled:opacity-30 disabled:pointer-events-none"
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>

              <div className="grid grid-cols-3 gap-2 flex-1 content-start">
                {yearPage.map((year) => {
                  const inRange = year >= MIN_YEAR && year <= MAX_YEAR
                  if (!inRange) {
                    return <div key={year} aria-hidden="true" />
                  }
                  return (
                    <button
                      type="button"
                      key={year}
                      onClick={() => handleYearSelect(year)}
                      className={cn(
                        "py-2 rounded-lg text-sm font-medium transition-all border",
                        year === currentYear
                          ? "bg-[#d4a017] text-[#1a2e20] border-transparent"
                          : "text-white/80 border-white/10 hover:bg-white/10 hover:border-white/20"
                      )}
                    >
                      {year}
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

Calendar.displayName = "Calendar"
export { Calendar }